import { constraintOptions, SeparatorOptionValues } from './constants';
import {
  type GlobalIdentifierObject,
  type GlobalIdentifierOperator,
  GlobalIdentifierPosition,
  type GlobalIdentifierSeparator,
  GlobalIdentifierSeparatorType,
  GlobalIdentifierSpecialChar,
  type LineItemComponentV2,
  LineItemOperatorType,
  type Option,
  ValueConstraints
} from '../../common/types';
import { v4 as uuid } from 'uuid';

export const generateGlobalIdentifierByName = (
  index: number,
  operator: GlobalIdentifierOperator
): GlobalIdentifierObject => {
  return {
    id: uuid(),
    name: `Identifier #${index + 1}`,
    description: '',
    rule: {
      operator,
      keyPattern: [],
      position: GlobalIdentifierPosition.ALL,
      separator: {
        type: GlobalIdentifierSeparatorType.SPECIAL_CHAR,
        value: GlobalIdentifierSpecialChar.LINE_BREAK,
        minCount: 1
      },
      valueConstraints: ValueConstraints.ALL,
      sharedWithOther: false,
      prefix: undefined,
      suffix: undefined
    },
    uploadToExternalService: true
  };
};

export const generateLineItemComponentByOperatorType = (operator: LineItemOperatorType): LineItemComponentV2 => {
  const { name, valueConstraints } = generateLabelByOperatorType(operator);
  return {
    id: uuid(),
    name,
    label: name,
    rule: {
      operator,
      keyPattern: [],
      position: GlobalIdentifierPosition.ALL,
      separator: {
        type: GlobalIdentifierSeparatorType.SPECIAL_CHAR,
        value: GlobalIdentifierSpecialChar.LINE_BREAK,
        minCount: 1
      },
      valueConstraints,
      sharedWithOther: false,
      prefix: {
        type: GlobalIdentifierSeparatorType.SINGLE_CHAR,
        value: '',
        minCount: 1
      },
      suffix: {
        type: GlobalIdentifierSeparatorType.SINGLE_CHAR,
        value: '',
        minCount: 1
      },
      isOptional: false,
      isMultiLine: false,
      order: 1
    },
    uploadToExternalService: true
  };
};

const generateLabelByOperatorType = (
  operator: LineItemOperatorType
): { name: string; valueConstraints: ValueConstraints } => {
  if (operator === LineItemOperatorType.PREDEFINED_NAME) {
    return { name: 'name', valueConstraints: ValueConstraints.ALL };
  }

  if (operator === LineItemOperatorType.PREDEFINED_QUANTITY) {
    return { name: 'quantity', valueConstraints: ValueConstraints.INTEGER };
  }

  if (operator === LineItemOperatorType.PREDEFINED_UNIT_PRICE) {
    return { name: 'unitPrice', valueConstraints: ValueConstraints.DECIMAL };
  }

  if (operator === LineItemOperatorType.PREDEFINED_PRICE) {
    return { name: 'price', valueConstraints: ValueConstraints.DECIMAL };
  }

  return { name: 'LineItem Component', valueConstraints: ValueConstraints.ALL };
};

export const generateSeparatorByItsType = (separatorType: SeparatorOptionValues): GlobalIdentifierSeparator => {
  if (separatorType === SeparatorOptionValues.SINGLE_SPACE) {
    return {
      type: GlobalIdentifierSeparatorType.SPECIAL_CHAR,
      value: GlobalIdentifierSpecialChar.SPACE,
      minCount: 1
    };
  }

  if (separatorType === SeparatorOptionValues.MULTIPLE_SPACE) {
    return {
      type: GlobalIdentifierSeparatorType.SPECIAL_CHAR,
      value: GlobalIdentifierSpecialChar.SPACE,
      minCount: 2
    };
  }

  if (separatorType === SeparatorOptionValues.SINGLE_LINE_BREAK) {
    return {
      type: GlobalIdentifierSeparatorType.SPECIAL_CHAR,
      value: GlobalIdentifierSpecialChar.LINE_BREAK,
      minCount: 1
    };
  }

  if (separatorType === SeparatorOptionValues.MULTIPLE_LINE_BREAK) {
    return {
      type: GlobalIdentifierSeparatorType.SPECIAL_CHAR,
      value: GlobalIdentifierSpecialChar.LINE_BREAK,
      minCount: 2
    };
  }

  return {
    type: GlobalIdentifierSeparatorType.NONE,
    value: '',
    minCount: 0
  };
};

export const isReadOnlyLabel = (name: string): boolean => {
  return ['name', 'quantity', 'price', 'unitPrice'].includes(name);
};

export const generateSelectOptionByKey = (key: ValueConstraints): Option | undefined => {
  return constraintOptions.find((constraint) => constraint.value === key);
};
